import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { conversationKeys } from "@/api/queries";
import { EVENT, resumeStream, streamMessage, type StreamEvent } from "@/api/stream";
import { FIRST_STEP, VISION_STEPS } from "./display";

/**
 * Suivre une réponse pendant qu'elle se construit (V1.5-04).
 *
 * Ce que ce hook affiche est un commentaire : la vérité est le message
 * persisté, que la liste relit à la fin. C'est pour ça qu'un flux perdu n'est
 * pas une erreur — on recharge la conversation et la réponse y est.
 *
 * Trois choses qu'il fait exprès :
 *
 * * il garde l'exécution en cours dans `sessionStorage`, pour se rebrancher
 *   après un rafraîchissement plutôt que de reposer la question ;
 * * il ignore un événement dont le rang n'avance pas — un rejeu ne doit pas
 *   faire clignoter l'écran deux fois ;
 * * il ne réessaie jamais tout seul un envoi : reposer une question sans le
 *   dire coûterait un second appel de modèle et pourrait doubler une réponse.
 */

type State = "idle" | "sending" | "thinking" | "looking" | "reading" | "writing";

interface Live {
  state: State;
  step: string;
  pendingQuestion: string | null;
  error: string | null;
  send: (content: string, clientMessageId: string, attachmentIds?: string[]) => Promise<void>;
}

interface ActiveRun {
  runId: string;
  lastEventId: string | null;
}

const STORAGE_PREFIX = "hdv.assistant.run.";

function remember(conversationId: string, run: ActiveRun | null): void {
  try {
    if (run) sessionStorage.setItem(STORAGE_PREFIX + conversationId, JSON.stringify(run));
    else sessionStorage.removeItem(STORAGE_PREFIX + conversationId);
  } catch {
    // Navigation privée, stockage bloqué : on perd la reprise, pas la réponse.
  }
}

function recall(conversationId: string): ActiveRun | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + conversationId);
    return raw ? (JSON.parse(raw) as ActiveRun) : null;
  } catch {
    return null;
  }
}

export function useAssistantStream(conversationId: string): Live {
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>("idle");
  const [step, setStep] = useState(FIRST_STEP);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sequence = useRef(0);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: conversationKeys.messages(conversationId) });
    void queryClient.invalidateQueries({ queryKey: conversationKeys.detail(conversationId) });
    void queryClient.invalidateQueries({ queryKey: conversationKeys.list() });
  }, [conversationId, queryClient]);

  const consume = useCallback(
    async (events: AsyncGenerator<StreamEvent>) => {
      for await (const event of events) {
        // Un rang qui n'avance pas est un doublon ou un événement d'avant :
        // on l'ignore plutôt que de rejouer l'affichage.
        if (event.sequence <= sequence.current) continue;
        sequence.current = event.sequence;
        remember(conversationId, { runId: event.run_id, lastEventId: event.event_id });

        switch (event.type) {
          case EVENT.accepted:
          case EVENT.started:
            setState("thinking");
            setStep(FIRST_STEP);
            break;
          case EVENT.attachment:
          case EVENT.visionStarted:
          case EVENT.barcodeCompleted:
          case EVENT.ocrCompleted:
          case EVENT.visionCompleted:
          case EVENT.matchingStarted:
          case EVENT.matchingCompleted:
            // La lecture d'une photo a son propre état : c'est la seule étape
            // qui dure assez longtemps pour qu'on croie l'écran figé.
            setState("looking");
            setStep(VISION_STEPS[event.type] ?? "Je regarde la photo…");
            break;
          case EVENT.toolStarted:
          case EVENT.toolCompleted:
            setState("reading");
            setStep(String(event.payload.label ?? FIRST_STEP));
            break;
          case EVENT.documentSearchStarted:
            setState("reading");
            setStep("Je consulte les documents enregistrés…");
            break;
          case EVENT.documentSearchCompleted: {
            // Le nombre d'extraits, tout de suite : « 0 extrait » et « 3
            // extraits » ne donnent pas la même attente sur la phrase qui suit.
            const found = Number(event.payload.result_count ?? 0);
            setStep(
              found > 0
                ? `${found} extrait${found > 1 ? "s" : ""} trouvé${found > 1 ? "s" : ""}…`
                : "Rien trouvé dans les documents…",
            );
            break;
          }
          case EVENT.toolFailed:
            setStep("Une lecture n'a pas abouti. Je continue avec le reste…");
            break;
          case EVENT.clarification:
            setStep("Il me manque une précision…");
            break;
          case EVENT.answerStarted:
            setState("writing");
            setStep("Je rédige la réponse…");
            break;
          case EVENT.failed:
            setError(String(event.payload.message ?? "HD-Vault n'a pas pu répondre."));
            break;
          default:
            break;
        }

        if (event.type === EVENT.completed || event.type === EVENT.failed) {
          remember(conversationId, null);
          sequence.current = 0;
          setState("idle");
          setPendingQuestion(null);
          refresh();
        }
      }
    },
    [conversationId, refresh],
  );

  // Reprise après rafraîchissement : on se rebranche sur l'exécution laissée
  // en cours. Le serveur rejoue ce qu'on a manqué et ne relance rien.
  useEffect(() => {
    const active = recall(conversationId);
    if (!active) return;
    const controller = new AbortController();
    setState("thinking");
    void (async () => {
      try {
        await consume(
          resumeStream(conversationId, active.runId, active.lastEventId, controller.signal),
        );
      } catch {
        // L'exécution n'est plus suivie : la réponse est en base.
        remember(conversationId, null);
        setState("idle");
        refresh();
      }
    })();
    return () => controller.abort();
  }, [conversationId, consume, refresh]);

  const send = useCallback(
    async (content: string, clientMessageId: string, attachmentIds?: string[]) => {
      setError(null);
      setPendingQuestion(content);
      setState("sending");
      sequence.current = 0;
      try {
        await consume(
          streamMessage(conversationId, {
            content,
            client_message_id: clientMessageId,
            attachment_ids: attachmentIds ?? [],
          }),
        );
      } catch (caught) {
        setState("idle");
        setPendingQuestion(null);
        throw caught;
      }
    },
    [conversationId, consume],
  );

  return { state, step, pendingQuestion, error, send };
}
